import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Chips, useDeviceType } from '@cian/ui-kit';

import { useChips, useScrollToActiveChip } from './hooks';
import { promosApplyChipsRequestAction } from '../../actions/chips';
import { DialogChipPopup } from '../../components/DialogChipPopup';
import { selectChips } from '../../selectors/chips';
import { selectIsCompilation } from '../../selectors/compilation';
import { IDialogFilterItem } from '../../types/filterSelectors';
import { IJsonQuery } from '../../types/jsonQuery/jsonQuery';
import { TThunkDispatch } from '../../types/redux/thunk';

const DROPDOWN_ANCHOR_ID = 'chip-dropdown-anchor';

export const ChipsContainer: React.FC = () => {
  const isPhone = useDeviceType() === 'phone';
  const dispatch: TThunkDispatch = useDispatch();
  const chips = useSelector(selectChips);
  const isCompilation = useSelector(selectIsCompilation);
  const [isPopupOpen, setPopupOpen] = React.useState(false);

  const [popupTitle, setPopupTitle] = React.useState<string>('');
  const [popupFilters, setPopupFilters] = React.useState<IDialogFilterItem[]>([]);
  const [popupChipRef, setPopupChipRef] = React.useState<React.RefObject<HTMLLIElement> | undefined>(undefined);
  const { options, value } = useChips(chips);

  useScrollToActiveChip(value, options);

  const openPopup = React.useCallback(
    (value: number) => {
      const chip = chips[value];
      const optionRef = options[value].ref as React.RefObject<HTMLLIElement>;

      if ('dialog' in chip && optionRef.current) {
        const anchor = document.querySelector(`#${DROPDOWN_ANCHOR_ID}`);

        if (anchor) {
          // Помещаем анкор в чипс, чтобы дропдаун отобразился под ним.
          optionRef.current.appendChild(anchor);
        }

        setPopupOpen(true);
        setPopupFilters(chip.dialog.filters);
        setPopupTitle(chip.dialog.title);
        setPopupChipRef(optionRef);
      }
    },
    [chips, options],
  );

  const closePopup = React.useCallback(() => {
    setPopupOpen(false);
    setPopupFilters([]);
    setPopupTitle('');
    setPopupChipRef(undefined);
  }, []);

  const handleFilterChange = React.useCallback(
    (jsonQuery: IJsonQuery, isActive: boolean) => {
      dispatch(promosApplyChipsRequestAction(jsonQuery, isActive));
    },
    [dispatch],
  );

  const handleChipClick = React.useCallback(
    (value: number, checked: boolean) => {
      const chip = chips[value];

      if ('dialog' in chip) {
        if (isPopupOpen) {
          closePopup();
        } else {
          openPopup(value);
        }
      } else {
        handleFilterChange(chip.jsonQuery, !checked);
      }
    },
    [chips, isPopupOpen, openPopup, closePopup, handleFilterChange],
  );

  if (isCompilation) {
    return null;
  }

  return (
    <div data-testid="ChipsList">
      <Chips options={options} value={value} onChipClick={handleChipClick} scrollable={isPhone} />
      <DialogChipPopup
        open={isPopupOpen}
        dialogFilters={popupFilters}
        dialogTitle={popupTitle}
        dropdownAnchor={<div id={DROPDOWN_ANCHOR_ID} style={{ visibility: 'hidden' }}></div>}
        chipRef={popupChipRef}
        onChange={handleFilterChange}
        onClose={closePopup}
      />
    </div>
  );
};
